import { Page } from '@ui/themed';
import { t } from 'i18next';
import { useHandlers, withPageProviders } from '../../../context/page-context';
import { CustomerGroupsQuickSetup } from '../components/customer-groups-quick-setup';
import { CUSTOMER_GROUP_BASE_SLUG } from '../constants';
import {
  CUSTOMER_GROUP_CREATE_METHOD_PATH_IDS,
  useLastCustomerGroupCreateMethod,
} from '../create-method';
import { handleCustomerGroupBuilderAxiosError } from '../handle-errors';
import { useCreateCustomerGroupWithSettings } from '../hooks/use-create-customer-group-with-settings';
import { useCreateUniqueSlugFromName } from '../hooks/use-create-unique-slug';
import {
  CustomerGroupsQuickSetupFormContext,
  useCustomerGroupsQuickSetupFormLogic,
} from '../hooks/use-customer-groups-quick-setup-form';
import { buildQuickSetupAdvancedQuery } from '../quick-setup-to-builder';
import type { CustomerGroupQuickSetupValidated } from '../types/customer-group-quick-setup.schema';

export const CustomerGroupsQuickSetupPage = withPageProviders(CustomerGroupsQuickSetupPageContent);

function CustomerGroupsQuickSetupPageContent() {
  const { navigationHandler, notificationHandler } = useHandlers();
  const { createUniqueSlugFromName } = useCreateUniqueSlugFromName();
  const [, setLastCustomerGroupCreateMethod] = useLastCustomerGroupCreateMethod();
  const { mutateAsync: createCustomerGroup } = useCreateCustomerGroupWithSettings({
    onSuccess: () => {
      setLastCustomerGroupCreateMethod('quick');
      notificationHandler.notify(
        t('notifications:titles.success'),
        t(
          'features:customer_group.pages.customer_groups_quick_setup_page.notifications.success.create_succeeded',
        ),
        'success',
      );
      navigationHandler.navigate('/customers/groups');
    },
    onError: (err) => {
      handleCustomerGroupBuilderAxiosError(err, notificationHandler, false);
    },
  });

  const form = useCustomerGroupsQuickSetupFormLogic({
    onSubmit: handleSubmit,
    onFailValidation: () => {
      notificationHandler.notify(
        t('notifications:titles.error'),
        t(
          'features:customer_group.pages.customer_groups_quick_setup_page.notifications.errors.validation_failed',
        ),
        'error',
      );
    },
  });

  async function handleSubmit(payload: CustomerGroupQuickSetupValidated) {
    const slug = createUniqueSlugFromName(payload.name);

    await createCustomerGroup({
      name: payload.name,
      slug,
      parent_slug: CUSTOMER_GROUP_BASE_SLUG,
      hasChildren: false,
      settings: payload.settings,
    });
  }

  return (
    <Page
      narrow
      title={t('features:customer_group.pages.customer_groups_quick_setup_page.header.title')}
      description={t(
        'features:customer_group.pages.customer_groups_quick_setup_page.header.description',
      )}
      backUrl="/customers/groups"
      secondaryActions={[
        {
          label: t(
            'features:customer_group.pages.customer_groups_quick_setup_page.header.actions.switch_to_advanced',
          ),
          onClick: () =>
            navigationHandler.navigateById(CUSTOMER_GROUP_CREATE_METHOD_PATH_IDS.advanced, {
              query: buildQuickSetupAdvancedQuery(form.state.values),
            }),
        },
      ]}
    >
      <CustomerGroupsQuickSetupFormContext.Provider value={form}>
        <CustomerGroupsQuickSetup
          onCancel={() => navigationHandler.navigate('/customers/groups')}
        />
      </CustomerGroupsQuickSetupFormContext.Provider>
    </Page>
  );
}
